import { useEffect, useMemo, useRef, useState } from 'react';
import {
  BatteryLowIcon,
  BellRingingIcon,
  CheckIcon,
  VideoCameraIcon,
} from '@phosphor-icons/react';
import Hls from 'hls.js';
import { apiFetch } from '../../../auth/apiFetch';
import { pollWithWake } from '../../../data/kioskRefresh';
import { getDeviceToken } from '../../../auth/deviceToken';

// =============================================================================
// STATUS — Ring widget is ENABLED (re-enabled 2026-06-05 after Pi 4 migration)
// =============================================================================
// History: this widget was disabled 2026-06-03 for two reasons — (1) the Pi 3B+
// (1 GB RAM) HA host was OOM-killing HA Core, and (2) the built-in Ring
// integration on the Pi 3 only served `camera.*_live_view` images while a
// motion/ding event was actively recording — idle cameras returned HTTP 500 —
// so the plan was to install the `ring-mqtt` add-on for proper snapshot
// entities.
//
// What actually happened: after migrating HA OS to a Pi 4 (4 GB), the idle-
// camera 500 no longer reproduces. All six `camera.*_live_view` entities now
// return valid 1080p+ JPEGs from idle via `/api/camera_proxy/` (HA's ffmpeg
// generates the still). So `ring-mqtt` turned out to be UNNECESSARY — the
// built-in integration + the existing entity IDs in this file work as-is.
// The ring-mqtt runbook below is kept only as a fallback in case Amazon ever
// breaks the built-in camera_proxy path again.
//
// =============================================================================
// FALLBACK RUNBOOK — only if the built-in camera_proxy path breaks again:
// =============================================================================
//
// STEP 1 — Migrate HA OS from Pi 3 to Pi 4
//   - Settings → System → Backups → create + download full backup
//   - Flash HA OS to Pi 4 storage, restore from backup on first boot
//   - Confirm Matter Server, ecobee, Meross, Linkind bulbs all still work
//
// STEP 2 — Install ring-mqtt
//   - HA → Settings → Add-ons → Add-on Store → ⋮ → Repositories → add
//     https://github.com/tsightler/ring-mqtt-ha-addon
//   - Install "Ring-MQTT with Video Streaming"
//   - Install Mosquitto MQTT broker if not already running, and set up the
//     MQTT integration in HA (auto-detected)
//   - Configure ring-mqtt: `snapshot_mode: all` for periodic snapshots
//   - Start add-on, complete Ring email + password + 2FA via the add-on web UI
//
// STEP 3 — Disable the built-in Ring integration
//   - Settings → Devices & Services → Ring (built-in) → Delete
//   - This is to avoid duplicate entities since ring-mqtt publishes its own
//
// STEP 4 — Update entity IDs in this file
//   - ring-mqtt names entities differently. Run the curl below from
//     apps/web/ to enumerate:
//       HA_BASE_URL=$(grep '^HA_BASE_URL=' .env.local | cut -d= -f2- | tr -d '"')
//       HA_LONG_LIVED_TOKEN=$(grep '^HA_LONG_LIVED_TOKEN=' .env.local | cut -d= -f2-)
//       curl -s -H "Authorization: Bearer $HA_LONG_LIVED_TOKEN" "$HA_BASE_URL/api/states" \
//         | jq -r '.[] | select(.attributes.friendly_name // "" | test("Backyard|Front Door|Driveway|Side|Dining Room|Living Room"; "i")) | .entity_id' \
//         | sort
//   - Expected new patterns from ring-mqtt:
//       image.<camera>_snapshot       (the snapshot entity — finally works)
//       binary_sensor.<doorbell>_ding (ring-mqtt may keep this as binary
//                                     instead of event entity — adjust the
//                                     ding-detection logic below if so)
//       sensor.<camera>_battery
//       sensor.<camera>_last_activity
//   - Update CAMERAS array below + DING_ENTITY_ID + the helper functions
//     (liveViewEntityId, batteryEntityId, lastActivityEntityId) to point at
//     the new IDs.
//   - Note: the image proxy in api/ha.ts allowlists `camera.*` only. If
//     ring-mqtt uses `image.*` entities, extend that allowlist to also
//     accept the `image.` prefix.
//
// STEP 5 — Re-enable the widget in KioskShell.tsx
//   - Uncomment `import { RingWidget } from './widgets/RingWidget'`
//   - Uncomment the `<RingWidget className="col-span-16" />` line
//
// =============================================================================
// WHAT WAS WORKING AT TIME OF DISABLE (confirmed live):
// =============================================================================
//   ✅ Layout: primary tile (flex-1) + 5 thumbnail column (w-72), h-[420px]
//   ✅ Tap thumbnail → swap primary + 15-min auto-revert
//   ✅ Audio unlock prompt + Web Audio chime synthesis
//   ✅ Battery sensor polling + low-battery icon overlay
//   ✅ Image fetch as blob → object URL → img src (preserves auth header)
//   ✅ Ding detection via event.front_door_ding state timestamp change
//   ✅ HA camera_proxy auth: requires per-camera access_token query param
//      (see fetchCameraImage in api/_lib/homeassistant.ts) — keep this in
//      mind if ring-mqtt has different auth semantics
//
// WHAT WAS NOT WORKING (and why ring-mqtt fixes it):
//   ❌ Snapshot images for idle cameras — `camera.*_live_view` only serves
//      images while a recording is active. ring-mqtt's `image.*_snapshot`
//      entities serve the periodic Snapshot-Capture images instead.
//
// =============================================================================
// Kitchen kiosk Ring cameras widget. Surfaces a primary camera image on the
// left (defaults to the driveway) and a column of thumbnails on
// the right for the other cameras. Tap a thumbnail to make it the primary;
// after 5 minutes of no interaction the primary reverts to the driveway.
//
// Live view: tapping a thumbnail OR the primary tile switches the primary to a
// live MJPEG feed (HA camera_proxy_stream via /api/ha?stream=) for LIVE_VIEW_MS,
// then auto-reverts to the cheap still. Only the primary ever streams — one Ring
// live session at a time — to bound bandwidth and battery drain. A pulsing red
// "Live View" badge marks the live window.
//
// Real-time signals:
//   - `event.front_door_ding` is polled at STATE_POLL_MS; when its state
//     timestamp changes we treat that as a fresh button press → flash a
//     "Doorbell ringing" badge, swap the primary to Front Door (if not
//     already), and play a synthesized ding-dong chime.
//   - `sensor.<x>_battery` is polled in the same call; thumbnails get a
//     low-battery icon when at or below LOW_BATTERY_THRESHOLD%.
//
// Images:
//   - Each camera's still image comes from /api/ha?image=camera.<x>_live_view
//     (proxied through HA's camera_proxy endpoint).
//   - Images are fetched as blobs and turned into object URLs so we get the
//     kiosk's device-token Authorization header on every request — img tags
//     with a plain src wouldn't forward it.
//   - Primary refreshes every PRIMARY_REFRESH_MS; thumbnails every
//     THUMB_REFRESH_MS to keep load down.
//
// Audio:
//   - The doorbell chime is always armed: the AudioContext is created on
//     mount and resumed immediately (kiosk Chromium launched with autoplay
//     allowed starts it 'running'), with a silent fallback that resumes on
//     the first interaction anywhere on the page — no visible prompt.
//     Web Audio API plays the chime synthesized from two sine
//     waves (C5 → A4) on each doorbell ding.

interface CameraDef {
  key: string;
  label: string;
}

// Outdoor cameras only. The two indoor cameras (Dining Room, Living Room)
// are intentionally omitted from the kiosk — they're right next to the
// kitchen, so a live feed of the room you're standing in adds nothing.
// Order is the left-to-right order of the single thumbnail row under the
// player (2026-09 vertical layout).
const CAMERAS: CameraDef[] = [
  { key: 'driveway', label: 'Driveway' },
  { key: 'front_door', label: 'Front Door' },
  { key: 'side', label: 'Side' },
  { key: 'deck', label: 'Deck' },
  { key: 'patio', label: 'Patio' },
];

const DOORBELL_KEY = 'front_door';
// The camera shown in the player by default (on mount and after the
// inactivity revert). Distinct from DOORBELL_KEY, which is what a ding
// swaps to and what the chime logic watches.
const DEFAULT_KEY = 'driveway';
const DING_ENTITY_ID = 'event.front_door_ding';
const PRIMARY_REFRESH_MS = 15_000;
const THUMB_REFRESH_MS = 60_000;
const STATE_POLL_MS = 3_000;
// After this much inactivity (no taps), the primary reverts to DEFAULT_KEY.
const REVERT_TIMEOUT_MS = 5 * 60 * 1000;
const DING_FLASH_MS = 8_000;
const LOW_BATTERY_THRESHOLD = 20;
// How long a tapped camera stays on the live MJPEG feed before reverting to
// the cheap still. Kept short on purpose — a live view holds a Ring session
// open (bandwidth + battery drain), so only the selected camera goes live and
// only for this window.
const LIVE_VIEW_MS = 45_000;
// When a camera reports motion (or a ding), pre-warm its live session for this
// long so a subsequent tap is instant (~0.2s) instead of paying the ~9s Ring
// cold-start. Kept under the cameras' ~180s motion-recording window so warming
// overlaps the period the camera is already awake — near-zero extra battery.
const WARM_AFTER_MOTION_MS = 90_000;
// Wired cameras (the patio + deck floodlight cams) have no battery to protect,
// so whenever there's ANY activity outside — motion on any camera, or a ding —
// we pre-warm both for a generous window. By the time you glance at the kiosk
// while something's happening, tapping them is instant. Battery cameras (the
// front door doorbell, and side/driveway on solar) are only warmed by their OWN
// motion or a tap, to spare the battery. We deliberately do NOT keep wired cams
// warm 24/7: Ring live streams from Ring's cloud (~1.5-2 Mbps), so continuous
// warming would burn tens of GB/day per camera.
const WIRED_CAMERAS = new Set(['patio', 'deck']);
const WARM_WIRED_MS = 3 * 60 * 1000;
// Any tap ANYWHERE in the kiosk means a person is at the screen — high odds
// they'll open a camera in the next minute. Warm the wired cams for this long
// so ring-mqtt has a head start on the ~5-8s cold Ring cold-start. Wired-only
// to keep the battery cameras charging; the window is short so a single stray
// tap doesn't hold a Ring session open for hours.
const WARM_ON_INTERACTION_MS = 60_000;

// Resting still: ring-mqtt's snapshot camera, which publishes a genuinely
// current frame on an interval (most cameras 30s; battery ones slower). This
// replaces the built-in `camera.*_live_view`, whose still was just the last
// recorded event's frame.
function snapshotEntityId(key: string): string {
  return `camera.${key}_snapshot`;
}
// Live feed: a Generic Camera entity (created in HA) pointing at ring-mqtt's
// on-demand live RTSP. HA serves it as live MJPEG via camera_proxy_stream, so
// the RTSP credential never leaves HA. Naming convention: `camera.<key>_live`.
function liveEntityId(key: string): string {
  return `camera.${key}_live`;
}
function batteryEntityId(key: string): string {
  return `sensor.${key}_battery`;
}
// ring-mqtt motion sensor — drives pre-warming so a tap during/after motion is
// instant. (Distinct from the built-in integration's stills.)
function motionEntityId(key: string): string {
  return `binary_sensor.${key}_motion`;
}
// HLS master-playlist URL for a camera's live feed, routed through the /api/ha
// proxy. The kiosk device token rides as the `auth` query param because hls.js
// fetches the playlist/segments without our Authorization header.
function liveHlsUrl(key: string): string {
  const token = getDeviceToken() ?? '';
  return `/api/ha?hls=${encodeURIComponent(
    liveEntityId(key),
  )}&auth=${encodeURIComponent(token)}`;
}

interface EntityState {
  entity_id: string;
  state: string;
  attributes: Record<string, unknown>;
  // ISO timestamp of the last HA state/attribute update. NOTE: this is NOT a
  // reliable "when was this image captured" signal — HA bulk-bumps it on
  // restarts/reconnects with no new frame behind it (observed 2026-09-20:
  // every snapshot entity jumped to the same last_updated while one camera's
  // frame was 2h old). For frame age, prefer the snapshot entity's
  // `attributes.timestamp` (epoch seconds ring-mqtt stamps on the actual
  // frame) via frameTimestampMs(); last_updated is only the fallback.
  last_updated: string;
}

interface HaResponse {
  states: Record<string, EntityState>;
}

// Fetches a camera's still image via apiFetch (so the device token's
// Authorization header rides along), turns the blob into an object URL,
// and revokes the previous one to avoid leaking memory across refreshes.
//
// URL includes a `_t=<refreshTick>` cache-buster query. The endpoint already
// responds with `Cache-Control: no-store`, but a stable URL across every
// poll still lets Chromium's in-memory cache (and any intermediate proxy)
// dedupe identical GETs and serve stale bytes — which is what left the
// thumbnails frozen on a two-hour-old frame even though HA had fresher
// ones. Varying the URL per tick forces each poll to actually re-fetch.
function useCameraImage(
  entityId: string,
  // Any change to this value triggers a refetch. Callers combine the periodic
  // tick with the HA snapshot entity's last_updated (as `${tick}:${iso}`) so a
  // freshly-published frame is fetched within one state-poll cycle rather than
  // waiting for the next periodic refresh — which is what made the "just now"
  // stamp read honestly (from last_updated) but the image still lag by up to
  // one refresh interval.
  refreshTick: number | string,
  enabled = true,
): string | null {
  const [url, setUrl] = useState<string | null>(null);
  const urlRef = useRef<string | null>(null);
  useEffect(() => {
    if (!enabled) return;
    let cancelled = false;
    apiFetch(
      `/api/ha?image=${encodeURIComponent(entityId)}&_t=${refreshTick}`,
    )
      .then((res) => (res.ok ? res.blob() : null))
      .then((blob) => {
        if (cancelled || !blob) return;
        const next = URL.createObjectURL(blob);
        const prev = urlRef.current;
        urlRef.current = next;
        setUrl(next);
        if (prev) URL.revokeObjectURL(prev);
      })
      .catch(() => {
        // Keep the previous frame if a single fetch fails — looks better
        // than flashing a placeholder.
      });
    return () => {
      cancelled = true;
    };
  }, [entityId, refreshTick, enabled]);
  // Final cleanup on unmount.
  useEffect(
    () => () => {
      if (urlRef.current) URL.revokeObjectURL(urlRef.current);
    },
    [],
  );
  return url;
}

// Synthesizes a two-tone "ding-dong" via Web Audio API — no audio file to
// ship, no CDN round-trip, identical sound on every browser. C5 then A4
// with soft envelopes so each note swells and decays naturally.
function playDoorbellChime(ctx: AudioContext): void {
  const now = ctx.currentTime;
  function note(freq: number, startOffset: number, durationS: number) {
    const osc = ctx.createOscillator();
    osc.type = 'sine';
    osc.frequency.value = freq;
    const gain = ctx.createGain();
    gain.gain.setValueAtTime(0, now + startOffset);
    gain.gain.linearRampToValueAtTime(0.35, now + startOffset + 0.02);
    gain.gain.exponentialRampToValueAtTime(0.001, now + startOffset + durationS);
    osc.connect(gain).connect(ctx.destination);
    osc.start(now + startOffset);
    osc.stop(now + startOffset + durationS);
  }
  note(523.25, 0, 0.6); // C5 — "ding"
  note(440, 0.3, 0.8); // A4 — "dong"
}

// When a camera's newest frame is older than this, its thumbnail gets an
// amber age badge. Battery cams refresh every 10 min (snapshot_mode "All" +
// 600s interval, set in ring-mqtt 2026-09-20) and wired ones every 30s, so
// anything past two battery cycles means the pipeline is stuck — surface it
// on the wall instead of silently showing an hours-old scene.
const STALE_FRAME_BADGE_MS = 21 * 60 * 1000;

// The actual capture time of a camera's current snapshot frame, in ms epoch.
// ring-mqtt stamps the frame's own time on the snapshot entity as
// `attributes.timestamp` (epoch seconds); entity last_updated is only a
// fallback because HA bulk-bumps it without new frames (see EntityState).
function frameTimestampMs(entity: EntityState | undefined): number | null {
  if (!entity) return null;
  const ts = entity.attributes?.timestamp;
  if (typeof ts === 'number' && ts > 0) return ts * 1000;
  const parsed = Date.parse(entity.last_updated ?? '');
  return Number.isFinite(parsed) ? parsed : null;
}

// Compact relative age, e.g. "just now", "12s ago", "3m ago", "1h ago".
function formatAgo(sinceMs: number, nowMs: number): string {
  const secs = Math.max(0, Math.round((nowMs - sinceMs) / 1000));
  if (secs < 5) return 'just now';
  if (secs < 60) return `${secs}s ago`;
  const mins = Math.floor(secs / 60);
  if (mins < 60) return `${mins}m ago`;
  const hours = Math.floor(mins / 60);
  return `${hours}h ago`;
}

interface RingWidgetProps {
  className?: string;
}

export function RingWidget({ className }: RingWidgetProps = {}) {
  const [activeKey, setActiveKey] = useState<string>(DEFAULT_KEY);
  const [states, setStates] = useState<Record<string, EntityState>>({});
  const [primaryTick, setPrimaryTick] = useState(0);
  const [thumbTick, setThumbTick] = useState(0);
  const [dingFlash, setDingFlash] = useState(false);
  // The camera currently showing the live feed (always the primary), or null
  // when everything is on cheap stills. Only ever one camera at a time.
  const [liveKey, setLiveKey] = useState<string | null>(null);
  // True once the live <video> is actually playing frames, so we can show a
  // "Connecting…" hint during the ~couple-second stream warm-up.
  const [liveReady, setLiveReady] = useState(false);
  const [nowTick, setNowTick] = useState(() => Date.now());

  const audioCtxRef = useRef<AudioContext | null>(null);
  const lastDingRef = useRef<string | null>(null);
  const revertTimerRef = useRef<ReturnType<typeof setTimeout> | null>(null);
  const liveTimerRef = useRef<ReturnType<typeof setTimeout> | null>(null);
  // Tracks which camera's live session is currently on, so clearLive() can
  // turn the right switch off even after activeKey has moved on.
  const liveKeyRef = useRef<string | null>(null);
  const videoRef = useRef<HTMLVideoElement | null>(null);
  // key → epoch-ms until which the camera should stay pre-warmed (set on
  // motion/ding). A camera's live_stream switch is on while it's live OR warm.
  const warmUntilRef = useRef<Map<string, number>>(new Map());
  // Cameras whose switch we've turned on (our intent), so reconcileStream only
  // POSTs on an actual change rather than every 3s poll.
  const streamOnRef = useRef<Set<string>>(new Set());

  // Entity-id list we poll for state every tick — single batched GET. The
  // snapshot entities are included solely so we can read their `last_updated`
  // to drive the primary's honest "Xs ago" stamp; we don't otherwise consume
  // their `state` (the image bytes come through /api/ha?image=…).
  const pollEntityIds = useMemo(() => {
    const ids = [DING_ENTITY_ID];
    for (const c of CAMERAS) {
      ids.push(batteryEntityId(c.key));
      ids.push(motionEntityId(c.key));
      ids.push(snapshotEntityId(c.key));
    }
    return ids;
  }, []);

  // Poll HA state at STATE_POLL_MS. On every poll, check for a new ding
  // by comparing event.front_door_ding's timestamp state to the last one
  // we saw; a changed value means a fresh button press.
  useEffect(() => {
    let alive = true;
    const url = `/api/ha?entities=${encodeURIComponent(pollEntityIds.join(','))}`;
    async function poll() {
      try {
        const res = await apiFetch(url);
        if (!alive || !res.ok) return;
        const data = (await res.json()) as HaResponse;
        if (!alive) return;
        setStates(data.states ?? {});
        const ding = data.states?.[DING_ENTITY_ID];
        if (ding) {
          const dingTime = ding.state;
          const validTimestamp =
            dingTime &&
            dingTime !== 'unknown' &&
            dingTime !== 'unavailable' &&
            !Number.isNaN(new Date(dingTime).getTime());
          if (validTimestamp) {
            if (lastDingRef.current === null) {
              // First successful poll — record but don't fire.
              lastDingRef.current = dingTime;
            } else if (dingTime !== lastDingRef.current) {
              lastDingRef.current = dingTime;
              handleDing();
            }
          }
        }
        // Pre-warm: any camera currently reporting motion gets its warm window
        // (re)extended, so a tap during/just after motion skips the cold start.
        const nowMs = Date.now();
        let anyMotion = false;
        for (const c of CAMERAS) {
          if (data.states?.[motionEntityId(c.key)]?.state === 'on') {
            warmUntilRef.current.set(c.key, nowMs + WARM_AFTER_MOTION_MS);
            anyMotion = true;
          }
        }
        // Any outdoor activity → keep the (free-to-warm) wired cams ready, so a
        // glance-and-tap while something's happening is instant.
        if (anyMotion) {
          for (const key of WIRED_CAMERAS) {
            warmUntilRef.current.set(key, nowMs + WARM_WIRED_MS);
          }
        }
        // Reconcile every camera's switch: turns warm cameras on and lets
        // expired ones (no longer live or warm) fall back off.
        reconcileAllStreams();
      } catch {
        // Silent: keep last known state, try again on next tick.
      }
    }
    void poll();
    const stop = pollWithWake(poll, STATE_POLL_MS);
    return () => {
      alive = false;
      stop();
    };
    // pollEntityIds is stable; intentionally excluding handleDing.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [pollEntityIds]);

  // Periodic image refresh ticks. pollWithWake also bumps these on wake so the
  // doorbell thumbnails refresh the moment the screen comes back, rather than
  // showing a frozen snapshot from before the display slept.
  useEffect(() => {
    const stop = pollWithWake(
      () => setPrimaryTick((x) => x + 1),
      PRIMARY_REFRESH_MS,
    );
    return stop;
  }, []);
  useEffect(() => {
    const stop = pollWithWake(
      () => setThumbTick((x) => x + 1),
      THUMB_REFRESH_MS,
    );
    return stop;
  }, []);

  // When the user swaps the primary, bump the primary tick so the new
  // camera fetches an image immediately rather than waiting for the
  // periodic interval.
  useEffect(() => {
    setPrimaryTick((x) => x + 1);
  }, [activeKey]);

  // Doorbell audio is always armed. Create the AudioContext on mount and
  // resume it immediately — a kiosk Chromium launched with autoplay allowed
  // (--autoplay-policy=no-user-gesture-required) starts 'running' right away.
  // For stricter browsers, the first interaction anywhere on the page resumes
  // it silently, so no visible "enable sound" prompt is ever needed.
  useEffect(() => {
    const AudioContextCtor =
      window.AudioContext ??
      (window as Window & { webkitAudioContext?: typeof AudioContext })
        .webkitAudioContext;
    if (!AudioContextCtor) return;
    const ctx = new AudioContextCtor();
    audioCtxRef.current = ctx;
    void ctx.resume().catch(() => {});
    const resume = () => {
      if (ctx.state === 'suspended') void ctx.resume().catch(() => {});
    };
    window.addEventListener('pointerdown', resume);
    window.addEventListener('keydown', resume);
    return () => {
      window.removeEventListener('pointerdown', resume);
      window.removeEventListener('keydown', resume);
      void ctx.close().catch(() => {});
    };
  }, []);

  // Clear the revert + live timers on unmount, and stop every session we
  // started (live or pre-warmed) so no camera is left streaming after the
  // kiosk navigates away.
  useEffect(
    () => () => {
      if (revertTimerRef.current) clearTimeout(revertTimerRef.current);
      if (liveTimerRef.current) clearTimeout(liveTimerRef.current);
      for (const key of streamOnRef.current) setLiveStream(key, false);
      streamOnRef.current.clear();
    },
    [],
  );

  // Presence-based pre-warm. Any pointerdown ANYWHERE in the kiosk (tapping a
  // light, scrolling the shopping list, opening media browser, …) means
  // someone's at the screen — good bet they'll open a camera in the next
  // minute. Extend the wired cams' warm window so ring-mqtt has a running
  // Ring session by the time a tap lands on a camera tile, cutting the
  // perceived cold-start from ~9s to ~1s. Uses capture so we still see
  // pointerdowns that a child stops from bubbling.
  useEffect(() => {
    const onInteraction = () => {
      const until = Date.now() + WARM_ON_INTERACTION_MS;
      let bumped = false;
      for (const key of WIRED_CAMERAS) {
        if ((warmUntilRef.current.get(key) ?? 0) < until) {
          warmUntilRef.current.set(key, until);
          bumped = true;
        }
      }
      if (bumped) reconcileAllStreams();
    };
    window.addEventListener('pointerdown', onInteraction, { capture: true });
    return () => {
      window.removeEventListener('pointerdown', onInteraction, { capture: true });
    };
    // reconcileAllStreams is stable (defined in this component, no deps).
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  function resetRevertTimer(): void {
    if (revertTimerRef.current) clearTimeout(revertTimerRef.current);
    revertTimerRef.current = setTimeout(() => {
      setActiveKey(DEFAULT_KEY);
    }, REVERT_TIMEOUT_MS);
  }

  // Low-level: flip ring-mqtt's `switch.<key>_live_stream`. ring-mqtt only
  // serves the live RTSP while this is on; turning it off ends the Ring
  // session (and the battery-draining wakefulness) right away.
  function setLiveStream(key: string, on: boolean): void {
    void apiFetch('/api/ha', {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({
        domain: 'switch',
        service: on ? 'turn_on' : 'turn_off',
        entity_id: `switch.${key}_live_stream`,
      }),
    }).catch(() => {
      // Best-effort; a failed toggle just means the stream may not start or
      // may linger until ring-mqtt's own idle timeout.
    });
  }

  // A camera's session should be on while it's the live tile OR still inside
  // its motion/ding warm window.
  function streamShouldBeOn(key: string): boolean {
    if (liveKeyRef.current === key) return true;
    return (warmUntilRef.current.get(key) ?? 0) > Date.now();
  }

  // Drive one camera's switch toward its desired state, POSTing only on a
  // change so the 3s poll doesn't spam HA.
  function reconcileStream(key: string): void {
    const want = streamShouldBeOn(key);
    const have = streamOnRef.current.has(key);
    if (want && !have) {
      streamOnRef.current.add(key);
      setLiveStream(key, true);
    } else if (!want && have) {
      streamOnRef.current.delete(key);
      setLiveStream(key, false);
    }
  }

  function reconcileAllStreams(): void {
    for (const c of CAMERAS) reconcileStream(c.key);
  }

  // Promote a camera to the primary tile and switch it to the live HLS feed
  // for LIVE_VIEW_MS, then auto-revert to the still. Calling it again (e.g.
  // tapping the live tile) just restarts the window. Only the tapped camera is
  // shown live, but tapping ANY camera pre-warms them ALL — once you're
  // browsing, every subsequent tap is instant instead of another ~9s cold
  // start. The warm windows lapse ~WARM_AFTER_MOTION_MS after the last tap.
  function startLive(key: string): void {
    liveKeyRef.current = key;
    setActiveKey(key);
    setLiveKey(key);
    const warmUntil = Date.now() + WARM_AFTER_MOTION_MS;
    for (const c of CAMERAS) warmUntilRef.current.set(c.key, warmUntil);
    reconcileAllStreams();
    resetRevertTimer();
    if (liveTimerRef.current) clearTimeout(liveTimerRef.current);
    liveTimerRef.current = setTimeout(() => clearLive(), LIVE_VIEW_MS);
  }

  function clearLive(): void {
    if (liveTimerRef.current) clearTimeout(liveTimerRef.current);
    liveTimerRef.current = null;
    const endedKey = liveKeyRef.current;
    liveKeyRef.current = null;
    setLiveKey(null);
    // Turns the (no-longer-live) camera off unless it's still warm.
    reconcileAllStreams();
    // Ring-mqtt suspends interval-based snapshot captures while a live
    // session is active (the camera can't serve both at once), so the
    // `camera.{key}_snapshot` entity is frozen on a frame from BEFORE the
    // live view started — which makes the reverted-to still look stale
    // (e.g. shows a person who, on the live feed, you just watched leave).
    // Kick a fresh capture and aggressively re-poll until it lands.
    if (endedKey) refreshSnapshotAfterLive(endedKey);
  }

  // Asks ring-mqtt to capture a fresh snapshot for `key` (via its
  // `button.{key}_take_snapshot` entity — verified against this household's
  // HA install, ring-mqtt publishes the press-to-snapshot button under
  // that name for every camera). The press returns ~1-2s later with a
  // fresh snapshot in `camera.{key}_snapshot`, so the +4s polling window
  // below catches it on the first tick.
  function refreshSnapshotAfterLive(key: string): void {
    void apiFetch('/api/ha', {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({
        domain: 'button',
        service: 'press',
        entity_id: `button.${key}_take_snapshot`,
      }),
    }).catch(() => {
      // Non-fatal: the polling below still catches the next interval
      // snapshot. If ring-mqtt renames the button entity on a future
      // version, update the entity_id above to match.
    });
    // primaryTick only affects whichever camera is currently the primary
    // — fine, since the user can only have looked at one camera live.
    const startedAt = Date.now();
    const REFRESH_WINDOW_MS = 45_000;
    const POLL_INTERVAL_MS = 4_000;
    const tick = setInterval(() => {
      if (Date.now() - startedAt >= REFRESH_WINDOW_MS) {
        clearInterval(tick);
        return;
      }
      setPrimaryTick((x) => x + 1);
    }, POLL_INTERVAL_MS);
  }

  function handleDing(): void {
    setActiveKey(DOORBELL_KEY);
    // A ding shows the still + chime — it doesn't auto-open live — but it does
    // pre-warm the doorbell (already awake recording the ding, so ~free despite
    // being on battery) and the wired cams, so walking over and tapping is
    // instant.
    clearLive();
    const warmUntil = Date.now() + WARM_WIRED_MS;
    warmUntilRef.current.set(DOORBELL_KEY, Date.now() + WARM_AFTER_MOTION_MS);
    for (const key of WIRED_CAMERAS) warmUntilRef.current.set(key, warmUntil);
    reconcileAllStreams();
    resetRevertTimer();
    setDingFlash(true);
    setTimeout(() => setDingFlash(false), DING_FLASH_MS);
    const ctx = audioCtxRef.current;
    if (!ctx) return;
    const play = () => {
      try {
        playDoorbellChime(ctx);
      } catch {
        // Swallow — chime failures shouldn't break the widget.
      }
    };
    // Play immediately if the context is live; otherwise resume first (e.g.
    // a browser that suspended it) and play once it's running.
    if (ctx.state === 'running') play();
    else void ctx.resume().then(play).catch(() => {});
  }

  function tapThumb(key: string): void {
    startLive(key);
  }

  const active = CAMERAS.find((c) => c.key === activeKey) ?? CAMERAS[0];
  const isPrimaryLive = liveKey === active.key;
  // Always keep a current snapshot for the primary — even while live — so it
  // can sit *under* the <video> as an instant placeholder during the ~couple-
  // second stream warm-up (the video is revealed only once it has live frames).
  // Refetch when EITHER the manual tick bumps (post-live re-poll, active
  // change) OR HA reports a new frame via last_updated. The latter keeps the
  // on-screen image no more than one state-poll cycle behind the honest
  // last_updated stamp; the former is a fallback if a state poll drops.
  const primarySnapshotUpdated = states[snapshotEntityId(active.key)]?.last_updated ?? '';
  const primaryImageUrl = useCameraImage(
    snapshotEntityId(active.key),
    `${primaryTick}:${primarySnapshotUpdated}`,
  );

  // "As of" stamp for the primary tile. Derived from the frame's own capture
  // timestamp (attributes.timestamp, last_updated fallback) rather than the
  // client fetch time so it reflects when ring-mqtt actually captured the
  // frame we're looking at. Prevents
  // the stamp from lying "just now" during the post-live catch-up window
  // when the fetch keeps succeeding but returns the same pre-live bytes.
  const primaryUpdatedAt = useMemo(
    () => frameTimestampMs(states[snapshotEntityId(active.key)]),
    [states, active.key],
  );

  // Tick every second so the relative "Xs ago" stamp stays current.
  useEffect(() => {
    const t = setInterval(() => setNowTick(Date.now()), 1_000);
    return () => clearInterval(t);
  }, []);

  // Drive the live <video> with hls.js while the primary is live. hls.js pulls
  // the playlist + fMP4 segments back through /api/ha (?hls= / ?hlsseg=). HA
  // serves these go2rtc cameras over (low-latency) HLS, not MJPEG. Network/
  // media errors self-recover; anything else reverts to the still.
  useEffect(() => {
    if (!isPrimaryLive) {
      setLiveReady(false);
      return;
    }
    const video = videoRef.current;
    if (!video) return;
    setLiveReady(false);
    // `loadeddata` (first frame decoded) or `playing` both mean we have a frame
    // to reveal — flip the badge to "Live View" on either.
    const onReady = () => setLiveReady(true);
    video.addEventListener('loadeddata', onReady);
    video.addEventListener('playing', onReady);
    let hls: Hls | null = null;
    if (Hls.isSupported()) {
      // Standard (not low-latency) HLS: more robust through our playlist proxy,
      // which doesn't forward the LL-HLS _HLS_msn/_HLS_part blocking params.
      // Tuned to show frames fast: start ~1 segment from the live edge, keep the
      // forward buffer small, and prefetch the first fragment instead of waiting
      // for the default 3-segment sync buffer.
      hls = new Hls({
        lowLatencyMode: false,
        backBufferLength: 10,
        liveSyncDurationCount: 1,
        maxBufferLength: 6,
        startFragPrefetch: true,
      });
      hls.loadSource(liveHlsUrl(active.key));
      hls.attachMedia(video);
      // Bounded recovery: retry a few transient errors, but if the stream just
      // won't come up (e.g. no `camera.<key>_live` entity yet → repeated 502s)
      // give up and fall back to the still instead of a perpetual "Connecting…".
      let recoverAttempts = 0;
      hls.on(Hls.Events.ERROR, (_evt, data) => {
        if (!data.fatal) return;
        if (recoverAttempts >= 3) {
          clearLive();
          return;
        }
        recoverAttempts += 1;
        if (data.type === Hls.ErrorTypes.NETWORK_ERROR) hls?.startLoad();
        else if (data.type === Hls.ErrorTypes.MEDIA_ERROR) hls?.recoverMediaError();
        else clearLive();
      });
    } else if (video.canPlayType('application/vnd.apple.mpegurl')) {
      video.src = liveHlsUrl(active.key); // Safari plays HLS natively
    } else {
      clearLive();
    }
    void video.play?.().catch(() => {});
    return () => {
      video.removeEventListener('loadeddata', onReady);
      video.removeEventListener('playing', onReady);
      if (hls) hls.destroy();
    };
    // active.key + isPrimaryLive fully determine the source; clearLive is stable.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isPrimaryLive, active.key]);

  return (
    <div
      className={`flex flex-col gap-4 min-h-0 overflow-hidden ${className ?? ''}`}
    >
      {/* Primary tile — full width of the widget at the cameras' native 16:9,
          the video-player layout (2026-09): player on top, thumbnail row
          below. Tapping it switches the shown camera to the live HLS feed for
          LIVE_VIEW_MS (same gesture as tapping a thumbnail). */}
      <button
        type="button"
        // Fire on pointerdown as well as click so ring-mqtt gets the ~100-300ms
        // head start between touch-down and touch-release on the ~5-8s Ring
        // cold-start. startLive is idempotent — calling it twice for a single
        // tap just sets the same state and restarts the same timer.
        onPointerDown={() => startLive(active.key)}
        onClick={() => startLive(active.key)}
        className="relative w-full aspect-video bg-subtle rounded-3xl overflow-hidden text-left cursor-pointer"
      >
        {isPrimaryLive ? (
          <>
            {/* Current snapshot sits underneath as an instant placeholder so
                the tile is never blank during the stream warm-up. */}
            {primaryImageUrl && (
              <img
                src={primaryImageUrl}
                alt={active.label}
                className="absolute inset-0 w-full h-full object-cover"
              />
            )}
            <video
              ref={videoRef}
              className={`absolute inset-0 w-full h-full object-cover transition-opacity duration-300 ${
                liveReady ? 'opacity-100' : 'opacity-0'
              }`}
              muted
              playsInline
              autoPlay
            />
          </>
        ) : primaryImageUrl ? (
          <img
            src={primaryImageUrl}
            alt={active.label}
            className="w-full h-full object-cover"
          />
        ) : (
          <div className="w-full h-full flex items-center justify-center text-fg-tertiary">
            <VideoCameraIcon size={64} weight="duotone" />
          </div>
        )}
        {/* Label bottom-left */}
        <div className="absolute bottom-3 left-3 px-3 py-1.5 bg-canvas/60 backdrop-blur-sm rounded-lg text-sm font-medium">
          {active.label}
        </div>
        {/* Bottom-right status badge: while live, "Connecting…" during the
            stream warm-up then the pulsing red "Live View" once frames flow;
            on the still, an "as-of" timestamp of the last snapshot refresh. */}
        {isPrimaryLive ? (
          <div className="absolute bottom-3 right-3 flex items-center gap-2 px-3 py-1.5 bg-canvas/60 backdrop-blur-sm rounded-lg text-sm font-medium">
            {liveReady ? (
              <>
                <span className="dot-red animate-pulse" />
                Live View
              </>
            ) : (
              <span className="text-fg-secondary">Connecting…</span>
            )}
          </div>
        ) : (
          primaryUpdatedAt && (
            <div className="absolute bottom-3 right-3 px-3 py-1.5 bg-canvas/60 backdrop-blur-sm rounded-lg text-sm font-medium text-fg-secondary">
              {formatAgo(primaryUpdatedAt, nowTick)}
            </div>
          )
        )}
        {/* Doorbell ringing overlay */}
        {dingFlash && (
          <>
            <div className="absolute inset-0 ring-4 ring-inset ring-amber animate-pulse pointer-events-none" />
            <div className="absolute top-3 left-3 right-3 flex items-center gap-2 px-3 py-2 bg-amber-soft border border-amber rounded-lg text-sm font-medium text-amber-light">
              <BellRingingIcon size={20} weight="fill" />
              Doorbell ringing
            </div>
          </>
        )}
      </button>

      {/* Thumbnail row — all five outdoor cameras in one row under the
          player, each at exact 16:9 (width-driven: five equal columns, tiles
          w-full + aspect-video). Tighter gap-2 + rounded-xl than the kiosk's
          usual card chrome: at this tile size the standard 16px gutter and
          24px radius ate too much image. The tile whose camera is in the
          primary slot gets a selected ring. */}
      <div className="grid grid-cols-5 gap-2">
        {CAMERAS.map((c) => {
          const battery = states[batteryEntityId(c.key)];
          const batteryPct = battery ? Number(battery.state) : Number.NaN;
          const lowBattery =
            !Number.isNaN(batteryPct) && batteryPct <= LOW_BATTERY_THRESHOLD;
          const snapshotUpdated = states[snapshotEntityId(c.key)]?.last_updated ?? '';
          // Honest-staleness badge: if the frame's own capture time is older
          // than the badge threshold, say so on the tile — a camera whose
          // snapshot pipeline stalls should announce itself, not keep showing
          // an hours-old scene as if it were current.
          const frameAt = frameTimestampMs(states[snapshotEntityId(c.key)]);
          const staleLabel =
            frameAt !== null && nowTick - frameAt > STALE_FRAME_BADGE_MS
              ? formatAgo(frameAt, nowTick)
              : null;
          return (
            <ThumbnailTile
              key={c.key}
              camera={c}
              refreshTick={thumbTick}
              snapshotUpdated={snapshotUpdated}
              onTap={tapThumb}
              lowBattery={lowBattery}
              staleLabel={staleLabel}
              selected={c.key === activeKey}
            />
          );
        })}
      </div>
    </div>
  );
}

interface ThumbnailTileProps {
  camera: CameraDef;
  refreshTick: number;
  // HA snapshot entity's last_updated ISO string. Combined with refreshTick
  // so a freshly-published frame drives a refetch within one state-poll,
  // rather than the tile drifting up to THUMB_REFRESH_MS behind reality.
  snapshotUpdated: string;
  onTap: (key: string) => void;
  lowBattery: boolean;
  // Non-null when the frame is older than STALE_FRAME_BADGE_MS — the
  // pre-formatted age ("2h ago") shown as an amber badge on the tile.
  staleLabel: string | null;
  selected: boolean;
}

function ThumbnailTile({
  camera,
  refreshTick,
  snapshotUpdated,
  onTap,
  lowBattery,
  staleLabel,
  selected,
}: ThumbnailTileProps) {
  const url = useCameraImage(
    snapshotEntityId(camera.key),
    `${refreshTick}:${snapshotUpdated}`,
  );
  return (
    <button
      type="button"
      // Pointerdown fires ~100-300ms before click on touch; use both so
      // ring-mqtt starts the Ring session that much earlier. onTap runs
      // startLive which is idempotent under a double-fire.
      onPointerDown={() => onTap(camera.key)}
      onClick={() => onTap(camera.key)}
      className="relative w-full aspect-video bg-subtle rounded-xl overflow-hidden hover:ring-2 hover:ring-border-strong transition-shadow text-left"
    >
      {url ? (
        <img
          src={url}
          alt={camera.label}
          className="w-full h-full object-cover"
        />
      ) : (
        <div className="w-full h-full flex items-center justify-center text-fg-tertiary">
          <VideoCameraIcon size={24} weight="duotone" />
        </div>
      )}
      {lowBattery && (
        <div className="absolute top-1 left-1 p-1 bg-amber-soft border border-amber rounded text-amber-light">
          <BatteryLowIcon size={14} weight="fill" />
        </div>
      )}
      {/* Stale-frame badge — the snapshot pipeline has stopped producing
          frames for this camera; show the frame's real age rather than
          letting an old scene pass as current. */}
      {staleLabel && (
        <div className="absolute top-1 right-1 px-1.5 py-0.5 bg-amber-soft border border-amber rounded text-amber-light text-[10px] font-medium leading-tight">
          {staleLabel}
        </div>
      )}
      {/* Selected state — the camera in the primary slot: dim the thumbnail
          and mark it with a centered check. Overlay sits above the snapshot. */}
      {selected && (
        <div className="absolute inset-0 flex items-center justify-center bg-canvas/50 pointer-events-none">
          <CheckIcon size={32} weight="bold" className="text-fg" />
        </div>
      )}
    </button>
  );
}
